<script lang="ts">
import type {
	SelectEmits,
	SelectItem,
	SelectSlots,
	SelectProps as USelectProps,
} from '#ui/types';
import type { ArrayOrNested, GetItemKeys } from '#ui/types/utils';

export interface SelectProps<T extends ArrayOrNested<SelectItem> = ArrayOrNested<SelectItem>, VK extends GetItemKeys<T> = 'value', M extends boolean = false> extends USelectProps<T, VK, M> {
	readonly?: boolean;
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<SelectItem>, VK extends GetItemKeys<T> = 'value', M extends boolean = false">
import { useForwardExpose, useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { useReadonly } from '#layers/ui/composables/use-readonly';
import Select from '#ui/components/Select.vue';

const { readonly, trailingIcon: trailingIconProp, ...forwardedProps } = defineProps<SelectProps<T, VK, M>>();
const emit = defineEmits<SelectEmits<T, VK, M>>();
const slots = defineSlots<SelectSlots<T, VK, M>>();
const { forwardRef } = useForwardExpose();
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);

const forwarded = useForwardPropsEmits(() => ({ ...forwardedProps, trailingIcon: trailingIconProp }), emit);
const trailingIcon = computed(() => !readonly ? trailingIconProp : '');
</script>

<template>
	<!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -- wraps Nuxt UI Select; the consuming form field supplies the label -->
	<Select
		:ref="forwardRef"
		v-bind="forwarded"
		:trailing-icon
		:data-readonly="readonly || undefined"
		@pointerdown.capture="preventPointer"
		@click.capture="preventPointer"
		@keydown.capture="preventKeyboardNavigation"
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
	</Select>
</template>
